import React, { useEffect, useState } from 'react';
import { useGate } from 'statsig-react';
import { useSwitchNetwork } from 'wagmi';

import {
  BaseIndicator,
  EthereumIndicator,
  OptimismIndicator,
} from '@components/Song/MintComponents/NetworkIndicator/ChainIndicators';
import { ModalButton } from '@components/main-components/modals/ModalButton';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { useConnectedAuth } from '@context/auth/connected';
import { useSecondaryModal } from '@context/secondaryModal';
import { useToast } from '@context/toast';
import { AVAILABLE_CHAIN_IDS, FEATURE_GATES } from '@utils/constants';

function useHandleChain({ chainId }: { chainId: number }) {
  const { closeCancelModal } = useSecondaryModal();

  const { setIsSwitchingNetwork, activeChainId } = useConnectedAuth();

  const isTargetChainActive = activeChainId.current === chainId;
  useEffect(() => {
    if (!isTargetChainActive) return;

    closeCancelModal();
  }, [isTargetChainActive, closeCancelModal]);

  useEffect(() => {
    if (isTargetChainActive) return;

    setIsSwitchingNetwork(true);

    return () => {
      setIsSwitchingNetwork(false);
    };
  }, [isTargetChainActive, setIsSwitchingNetwork]);
}

export type ChainType = keyof typeof AVAILABLE_CHAIN_IDS;

export type OnSwitchSuccess = (info: { chain: ChainType }) => void;

export function SwitchChainModal({
  onSwitchSuccess,
  chain,
}: {
  onSwitchSuccess: OnSwitchSuccess | undefined;
  chain: ChainType;
}) {
  const { closeCancelModal } = useSecondaryModal();
  const { switchNetworkAsync } = useSwitchNetwork();
  const { openToast } = useToast();
  const { value: baseChain } = useGate(FEATURE_GATES.BASE_CHAIN);

  useHandleChain({
    chainId: AVAILABLE_CHAIN_IDS[chain],
  });

  const [isLoading, setIsLoading] = useState(false);

  const networkLabel = (() => {
    switch (chain) {
      case 'eth':
        return 'Ethereum';
      case 'optimism':
        return 'Optimism';
      case 'base':
        return 'Base';
      default:
        return 'Unsupported Network';
    }
  })();

  const leadingIcon = (() => {
    switch (chain) {
      case 'eth':
        return <EthereumIndicator inverted />;
      case 'optimism':
        return <OptimismIndicator inverted />;
      case 'base':
        return baseChain ? <BaseIndicator inverted /> : null;
      default:
        // Unsupported chain there is no icon
        return <div />;
    }
  })();

  return (
    <StandardModal
      closeModal={closeCancelModal}
      title="Switch network"
      bodyWithoutPaddingBottom
      body={
        <div className="flex flex-col gap-6">
          <p className="font-base text-base800">
            You are currently on a different network. Connect your wallet to the {networkLabel}{' '}
            Network to continue.
          </p>
          <ModalButton
            label={`Switch to ${networkLabel}`}
            variant="primary"
            disabled={isLoading}
            loading={isLoading}
            leadingIcon={leadingIcon}
            onClick={() => {
              if (!switchNetworkAsync) {
                openToast({
                  text: `Connected wallet doesn't support switching network, please try connecting another wallet to continue`,
                  variant: 'error',
                });

                return;
              }

              setIsLoading(true);

              let clickLoading = true;

              const loadingOffTimeout = setTimeout(loadingOff, 2000);

              function loadingOff() {
                clearTimeout(loadingOffTimeout);

                if (clickLoading) setIsLoading((clickLoading = false));
              }

              switchNetworkAsync(AVAILABLE_CHAIN_IDS[chain])
                .then(() => {
                  onSwitchSuccess?.({ chain });
                })
                .catch(() => null)
                .finally(loadingOff);
            }}
          />
        </div>
      }
    />
  );
}
